import { LicenseProvider } from "@coach/license-client";
import { configureWebSqlWasm } from "@coach/sync/adapters/web";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Konva from "konva";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";
// Vite resolves this to a hashed URL (e.g. `/assets/sql-wasm-DEADBEEF.wasm`)
// that lives outside any service worker's navigateFallback reach.
import sqlWasmUrl from "sql.js/dist/sql-wasm.wasm?url";
import { isTauri } from "./lib/local-media.js";
import "./index.css";
import { App } from "./App.js";
import { AppErrorBoundary } from "./components/error-boundary.js";
import { SyncProvider } from "./providers/sync-provider.js";

configureWebSqlWasm(sqlWasmUrl);

// ── Cap on-screen canvas resolution ─────────────────────────────────────────
// Konva defaults every layer canvas to `window.devicePixelRatio`. On high-DPR
// screens (3× phones, some tablets) a full-screen tactics/whiteboard stage
// becomes a huge canvas that is expensive to repaint on every pointer sample.
// Cap the on-screen ratio at 2 to bound that fill cost. Export/thumbnail paths
// pass their own explicit `pixelRatio` to `toCanvas`/`toDataURL`, so print
// quality is unaffected by this global default.
if (typeof window !== "undefined") {
  Konva.pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
}

// ── Reduced-effects switch (`?fx=off` / `?fx=on`) ───────────────────────────
// Kill switch for the GPU-heavy Kinetic-Glass effects (animated blur sweep,
// backdrop-filter surfaces). iPad GPUs (verified on iPadOS 17.7) wedge for
// minutes compositing them — the animated blur layer under several
// backdrop-filter surfaces forces a full-viewport blur pipeline every frame,
// with zero console output. CSS overrides live in `index.css` under
// `html[data-fx="off"]`.
//
// Resolution order:
//   1. Explicit `?fx=off|on` URL param — persisted to localStorage so the
//      installed PWA keeps the user's choice across launches.
//   2. Stored previous explicit choice.
//   3. Auto-detect: iPads default to reduced effects. iPadOS Safari reports
//      a desktop-Mac UA by default ("Request Desktop Website"), so a Mac UA
//      with real touch points is an iPad, not a Mac.
if (typeof window !== "undefined") {
  const FX_KEY = "coach.fx";
  try {
    const param = new URLSearchParams(window.location.search).get("fx");
    if (param === "off" || param === "on") localStorage.setItem(FX_KEY, param);
    const stored = localStorage.getItem(FX_KEY);
    const isIpad =
      /\biPad\b/.test(navigator.userAgent) ||
      (navigator.userAgent.includes("Macintosh") && navigator.maxTouchPoints > 1);
    // Detected defaults are NOT persisted — only explicit ?fx= choices are —
    // so improving the heuristic later takes effect without users clearing
    // their storage.
    const fx = stored ?? (isIpad ? "off" : "on");
    if (fx === "off") document.documentElement.dataset.fx = "off";
  } catch {
    /* localStorage unavailable (private mode) — default to full effects */
  }
}

// ── `crypto.randomUUID` polyfill ────────────────────────────────────────────
// Tauri's WRY WebView on Windows/Linux and older Android WebViews ship a
// `crypto` object without `randomUUID` (it requires a secure context, which
// `tauri://` is not always classified as). Many call sites in the app use
// `crypto.randomUUID()` — including the login flow — so install a v4 fallback
// built on `crypto.getRandomValues` before any module touches it.
(() => {
  const c = globalThis.crypto as Crypto | undefined;
  if (!c || typeof c.randomUUID === "function") return;
  // RFC 4122 v4 from `getRandomValues` (always available in WebViews).
  const fallback = (): `${string}-${string}-${string}-${string}-${string}` => {
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    // Per spec: set version (4) and variant (10xx) bits.
    bytes[6] = (bytes[6]! & 0x0f) | 0x40;
    bytes[8] = (bytes[8]! & 0x3f) | 0x80;
    const hex: string[] = [];
    for (let i = 0; i < 16; i++) hex.push(bytes[i]!.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
      .slice(6, 8)
      .join(
        "",
      )}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}` as `${string}-${string}-${string}-${string}-${string}`;
  };
  try {
    Object.defineProperty(c, "randomUUID", {
      value: fallback,
      configurable: true,
      writable: true,
    });
  } catch {
    // Last resort: replace whole crypto reference (Safari guards `crypto`).
    (globalThis as { crypto: Crypto }).crypto = new Proxy(c, {
      get(t, p) {
        if (p === "randomUUID") return fallback;
        const v = (t as unknown as Record<PropertyKey, unknown>)[p];
        return typeof v === "function" ? (v as Function).bind(t) : v;
      },
    }) as Crypto;
  }
})();

// ── `Map.prototype.getOrInsert{,Computed}` polyfill ────────────────────────
// PDF.js 5.x (pdfjs-dist) uses the TC39 "upsert" Map methods
// (`getOrInsert` / `getOrInsertComputed`) that only ship in V8 ~12.5+.
// VS Code's built-in Simple Browser and older WebKit/WebViews don't have
// them yet, so opening a PDF fails with
//   "this[#methodPromises].getOrInsertComputed is not a function".
// Install a tiny polyfill before any module imports pdfjs-dist.
(() => {
  const proto = Map.prototype as Map<unknown, unknown> & {
    getOrInsert?: (key: unknown, value: unknown) => unknown;
    getOrInsertComputed?: (key: unknown, fn: (key: unknown) => unknown) => unknown;
  };
  if (typeof proto.getOrInsertComputed !== "function") {
    Object.defineProperty(proto, "getOrInsertComputed", {
      value: function getOrInsertComputed(
        this: Map<unknown, unknown>,
        key: unknown,
        fn: (key: unknown) => unknown,
      ) {
        if (this.has(key)) return this.get(key);
        const value = fn(key);
        this.set(key, value);
        return value;
      },
      configurable: true,
      writable: true,
    });
  }
  if (typeof proto.getOrInsert !== "function") {
    Object.defineProperty(proto, "getOrInsert", {
      value: function getOrInsert(this: Map<unknown, unknown>, key: unknown, value: unknown) {
        if (this.has(key)) return this.get(key);
        this.set(key, value);
        return value;
      },
      configurable: true,
      writable: true,
    });
  }
  // Same for WeakMap (pdfjs uses both).
  const wproto = WeakMap.prototype as WeakMap<object, unknown> & {
    getOrInsert?: (key: object, value: unknown) => unknown;
    getOrInsertComputed?: (key: object, fn: (key: object) => unknown) => unknown;
  };
  if (typeof wproto.getOrInsertComputed !== "function") {
    Object.defineProperty(wproto, "getOrInsertComputed", {
      value: function getOrInsertComputed(
        this: WeakMap<object, unknown>,
        key: object,
        fn: (key: object) => unknown,
      ) {
        if (this.has(key)) return this.get(key);
        const value = fn(key);
        this.set(key, value);
        return value;
      },
      configurable: true,
      writable: true,
    });
  }
  if (typeof wproto.getOrInsert !== "function") {
    Object.defineProperty(wproto, "getOrInsert", {
      value: function getOrInsert(this: WeakMap<object, unknown>, key: object, value: unknown) {
        if (this.has(key)) return this.get(key);
        this.set(key, value);
        return value;
      },
      configurable: true,
      writable: true,
    });
  }
})();

// In dev, evict any service worker installed by a previous session. Otherwise
// a stale workbox SW keeps returning index.html for `/sql-wasm.wasm` (and
// other static assets) → WebAssembly.compile fails with the infamous
// "expected magic word 00 61 73 6d, found 3c 21 64 6f" error.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  void navigator.serviceWorker.getRegistrations().then(async (regs) => {
    if (regs.length === 0) return;
    await Promise.all(regs.map((r) => r.unregister()));
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    // Reload once so the next page load is uncached.
    if (!sessionStorage.getItem("sw-cleared")) {
      sessionStorage.setItem("sw-cleared", "1");
      location.reload();
    }
  });
}

// ── PWA service-worker registration ─────────────────────────────────────────
// Only register when we're running as a real web build:
//   - In dev: skipped (the block above tears down any leftover SW).
//   - In Tauri: skipped — `tauri://localhost` is a custom protocol the SW
//     would never intercept, and a stale SW could mask shell updates.
// On success we expose the returned `updateSW(true)` reloader on
// `window.__updateSW__` so the Phase-2 update toast can call it without
// re-importing the virtual module.
if (import.meta.env.PROD && !isTauri()) {
  void (async () => {
    try {
      const { registerSW } = await import("virtual:pwa-register");
      const updateSW = registerSW({
        immediate: true,
        onNeedRefresh() {
          // A newer SW has finished installing and is waiting to take over.
          // Phase 2 surfaces this in a glass-floating toast; for now we keep
          // a global so the toast component can pick it up without prop drilling.
          console.info("[pwa] update verfügbar");
          window.dispatchEvent(new CustomEvent("pwa:update-available"));
        },
        onOfflineReady() {
          console.info("[pwa] offline-bereit");
          window.dispatchEvent(new CustomEvent("pwa:offline-ready"));
        },
        onRegisterError(err) {
          console.error("[pwa] SW-Registration fehlgeschlagen:", err);
        },
      });
      (
        window as unknown as {
          __updateSW__?: (reload?: boolean) => Promise<void>;
        }
      ).__updateSW__ = updateSW;
    } catch (err) {
      console.error("[pwa] SW konnte nicht initialisiert werden:", err);
    }
  })();

  // Ask the browser to mark our origin as persistent so it doesn't evict
  // OPFS media / IndexedDB under storage pressure. Best-effort — Chrome may
  // grant silently after install, Firefox prompts. Failure is non-fatal.
  void (async () => {
    try {
      if (
        typeof navigator !== "undefined" &&
        navigator.storage &&
        typeof navigator.storage.persist === "function"
      ) {
        const already = await navigator.storage.persisted?.();
        if (!already) {
          const granted = await navigator.storage.persist();
          console.info(`[pwa] persistent storage ${granted ? "gewährt" : "abgelehnt"}`);
        }
      }
    } catch (e) {
      console.warn("[pwa] storage.persist konnte nicht angefordert werden:", e);
    }
  })();
}

// Public Ed25519 verify key — shipped at build time. Server is the only signer.
const PUBLIC_LICENSE_KEY = import.meta.env.VITE_LICENSE_VERIFY_PUBLIC_KEY ?? "";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Offline-first: a stale cache is better than nothing.
      staleTime: 30_000,
      gcTime: 24 * 60 * 60 * 1000,
      retry: 2,
      networkMode: "offlineFirst",
    },
    mutations: { networkMode: "offlineFirst" },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <LicenseProvider publicKey={PUBLIC_LICENSE_KEY}>
          <SyncProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </SyncProvider>
        </LicenseProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
